'use client';

import { usePathname } from 'next/navigation';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { Link } from '@/app_components/common/Link';
import { BasicPopover } from '@/app_components/ui/BasicPopover';
import type { MenuItem } from '@/app_lib/types/Menu';
import type { NestedMenuItem } from '@/app_lib/types/NestedMenuItem';

const HOMEPAGES = new Set(['/', '/recommends/', '/crypto/', '/well/', '/asia/']);

const trackingData = {
  data: {
    // biome-ignore lint/style/useNamingConvention: <external data>
    contentPlacementCD: 'topic navigation',
    eventAction: 'topic navigation',
    eventCategory: 'navigation',
  },
};

function toPathname(urlOrPath: string): string {
  try {
    return new URL(urlOrPath).pathname;
  } catch {
    return urlOrPath;
  }
}

function normalize(urlOrPath: string): string {
  return toPathname(urlOrPath).replace(/\/+$/, '') || '/';
}

function isMatch(pathname: string, targetUrl: string): boolean {
  const basePath = normalize(pathname.replace(/\/page\/\d+\/?$/, '/'));
  const target = normalize(targetUrl);
  const targetWithSlash = target === '/' ? '/' : `${target}/`;

  if (HOMEPAGES.has(targetWithSlash)) {
    return basePath === target;
  }

  return basePath === target || basePath.startsWith(`${target}/`);
}

const SubMenuContent = ({ subMenu, label }: { subMenu: MenuItem[]; label: string }) =>
  subMenu?.length > 0 && (
    <ul
      className='hidden lg:flex flex-col gap-2 min-w-59 p-4 mt-4 bg-pure shadow'
      data-cy={`submenu-${label.toLocaleLowerCase().replaceAll(' ', '-')}`}
    >
      {subMenu.map((subMenuItem) => (
        <li key={`${subMenuItem.id}-${subMenuItem.label}`}>
          <Link
            className='hover:text-neutral-600 focus:text-accent-primary'
            href={subMenuItem.url}
          >
            {subMenuItem.label}
          </Link>
        </li>
      ))}
    </ul>
  );

// Radix Popover.Trigger injects its own className via cloneElement — destructure
// it out so it doesn't overwrite our active-state classes through the rest spread.
const NavBarLinkAnchor = forwardRef<
  HTMLAnchorElement,
  { label: string; url: string; active: boolean } & ComponentPropsWithoutRef<'a'>
>(({ label, url, active, className: _popoverClassName, ...triggerProps }, ref) => (
  <Link
    aria-current={active ? 'page' : undefined}
    className={`hover:border-b-2 hover:border-red-500 ${active ? 'border-b-2 border-red-500' : ''}`}
    data-cy={label.toLocaleLowerCase().replaceAll(' ', '-')}
    href={url}
    optimizelyTrackingData={{ name: 'navigation_topic_menu_click' }}
    ref={ref}
    trackingData={{
      data: {
        ...trackingData.data,
        eventAction: `${trackingData.data.eventAction} - level 0`,
        eventLabel: label,
      },
    }}
    {...triggerProps}
  >
    {label}
  </Link>
));

export const NavBarItems = ({ items }: { items: NestedMenuItem[] }) => {
  const pathname = usePathname() ?? '/';

  return items.map((item) => {
    const subMenuUrls = item.subMenu?.map((sub) => sub.url) ?? [];
    const active = isMatch(pathname, item.url) || subMenuUrls.some((subUrl) => isMatch(pathname, subUrl));

    return (
      <li key={`${item.id}-${item.label}`}>
        <BasicPopover
          content={
            <SubMenuContent
              label={item.label}
              subMenu={item.subMenu}
            />
          }
          trigger={
            <NavBarLinkAnchor
              active={active}
              label={item.label}
              url={item.url}
            />
          }
        />
      </li>
    );
  });
};
